// src/pages/inventory/PurchaseInvoice/PathTransactionHistory.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
} from "@mui/material";
import {
  getAllPathologyTransactions,
  isPathologyTransactionApiUnavailable,
} from "../../../services/pathologyTransactionService";
import { getInvoiceList } from "../../../services/invoiceService";
import { getAllSuppliers } from "../../../services/supplierService";
import { getAllPatients } from "../../../services/patientService";
import { getAllDoctors } from "../../../services/doctorService";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import Refresh from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";
import { addOrgToUrl } from "../../../utils/organizationUtils";
import { buildPatientTimelineNavigation } from "../../../utils/patientTimelineVisitUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import { isPathModuleStaffRole } from "../../../utils/pathRoleUtils";
import {
  getPathologyTransactionInvoiceId,
  getPathologyTransactionItemName,
  getPathologyTransactionLabTestId,
  getPathologyTransactionPatientId,
  getPathologyTransactionPrescriptionId,
  getPathologyTransactionDoctorName,
  getPathologyTransactionQuantity,
  getPathologyTransactionSupplierName,
  isPathologyTestInvoice,
  resolveSupplierDisplayName,
} from "../../../utils/pathTransactionHistoryUtils";

const clickableIdSx = {
  color: "#568AF7",
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 500,
};

const columns = [
  { id: "itemName", label: "Item Name" },
  { id: "batchNumber", label: "Batch Number" },
  { id: "unitType", label: "Unit Type" },
  { id: "deductedQuantity", label: "Deducted/Added Qty" },
  { id: "transactionType", label: "Transaction Type" },
  { id: "transactionDate", label: "Transaction Date" },
  { id: "patientName", label: "Patient" },
  { id: "doctorName", label: "Doctor" },
  { id: "supplierName", label: "Supplier Name" },
  { id: "invoiceId", label: "Invoice ID" },
  { id: "prescriptionId", label: "Prescription ID" },
  { id: "labTestId", label: "Lab Test ID" },
  { id: "costPerUnit", label: "Cost/Unit" },
];

const unwrapInvoiceList = (invoices) => {
  if (Array.isArray(invoices)) return invoices;
  if (Array.isArray(invoices?.data)) return invoices.data;
  if (Array.isArray(invoices?.content)) return invoices.content;
  return [];
};

const unwrapPeopleList = (rows) => {
  if (Array.isArray(rows)) return rows;
  if (Array.isArray(rows?.data)) return rows.data;
  if (Array.isArray(rows?.content)) return rows.content;
  return [];
};

const isBlankDisplay = (value) => {
  if (value == null || value === "") return true;
  const normalized = String(value).trim().toLowerCase();
  return (
    !normalized ||
    normalized === "n/a" ||
    normalized === "na" ||
    normalized === "-" ||
    normalized === "—" ||
    normalized === "null" ||
    normalized === "undefined"
  );
};

const displayText = (value) => (isBlankDisplay(value) ? "" : String(value).trim());

const personDisplayName = (person) => {
  if (!person || typeof person !== "object") return "";
  return (
    displayText(person.name) ||
    displayText(person.doctorName) ||
    displayText(person.patientName) ||
    displayText(person.fullName) ||
    [person.firstName, person.lastName].filter(Boolean).join(" ").trim()
  );
};

const PathTransactionHistory = ({
  inventoryGroups,
  onViewInventoryBatch,
  onViewLabTest,
} = {}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterItem, setFilterItem] = useState("");
  const [filterBatch, setFilterBatch] = useState("");
  const [filterUnitType, setFilterUnitType] = useState("");
  const [filterPatient, setFilterPatient] = useState("");
  const [filterPatientType, setFilterPatientType] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryGroups?.length]);

  useEffect(() => {
    filterTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transactions,
    searchQuery,
    filterType,
    filterItem,
    filterBatch,
    filterUnitType,
    filterPatient,
    filterPatientType,
    filterDoctor,
    filterSupplier,
    fromDate,
    toDate,
  ]);

  const enrichWithSupplier = async (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    let invoiceById = new Map();
    let invoiceByPrescriptionId = new Map();
    let invoiceByPatientId = new Map();
    let supplierById = new Map();
    let patientById = new Map();
    let doctorById = new Map();
    const inventoryById = new Map();
    const inventoryByItem = new Map();

    (Array.isArray(inventoryGroups) ? inventoryGroups : []).forEach((group) => {
      (group?.records || []).forEach((record) => {
        if (record?.id != null) inventoryById.set(String(record.id), record);
        const itemKey = String(group?.name || record?.name || "")
          .trim()
          .toLowerCase();
        if (itemKey && !inventoryByItem.has(itemKey)) {
          inventoryByItem.set(itemKey, record);
        }
      });
    });

    // Avoid loading /prescriptions (very slow). Prefer names already on the
    // transaction row, then invoices + suppliers (same pattern as medicine history).
    // Doctors/patients are loaded only when names are still missing.
    const needsPatientLookup = list.some((tx) => {
      if (!isBlankDisplay(tx?.patientName) || !isBlankDisplay(tx?.patient_name)) {
        return false;
      }
      return getPathologyTransactionPatientId(tx) != null;
    });
    const needsDoctorLookup = list.some((tx) => {
      if (getPathologyTransactionDoctorName(tx)) return false;
      return (
        tx?.doctorId != null ||
        tx?.doctor_id != null ||
        tx?.assignedBy != null
      );
    });

    try {
      const [invoices, suppliers, patients, doctors] = await Promise.all([
        getInvoiceList({ fullRange: true }).catch(() => []),
        getAllSuppliers().catch(() => []),
        needsPatientLookup ? getAllPatients().catch(() => []) : Promise.resolve([]),
        needsDoctorLookup ? getAllDoctors().catch(() => []) : Promise.resolve([]),
      ]);

      unwrapInvoiceList(invoices).forEach((inv) => {
        if (inv?.id == null) return;
        invoiceById.set(String(inv.id), inv);
        const prescriptionId =
          inv.prescriptionId ??
          inv.prescription_id ??
          inv.prescription?.id ??
          null;
        if (
          prescriptionId != null &&
          isPathologyTestInvoice(inv) &&
          !invoiceByPrescriptionId.has(String(prescriptionId))
        ) {
          invoiceByPrescriptionId.set(String(prescriptionId), inv);
        }
        const patientId = inv.patientId ?? inv.patient_id ?? inv.patient?.id ?? null;
        if (
          patientId != null &&
          isPathologyTestInvoice(inv) &&
          !invoiceByPatientId.has(String(patientId))
        ) {
          invoiceByPatientId.set(String(patientId), inv);
        }
      });

      supplierById = new Map(
        unwrapPeopleList(suppliers)
          .filter((s) => s?.id != null)
          .map((s) => [
            String(s.id),
            resolveSupplierDisplayName(s) ||
              resolveSupplierDisplayName(s.name) ||
              resolveSupplierDisplayName(s.supplierName),
          ])
      );

      unwrapPeopleList(patients)
        .filter((p) => p?.id != null)
        .forEach((p) => {
          patientById.set(String(p.id), {
            name: personDisplayName(p),
            doctorName: displayText(p.doctorName) || displayText(p.doctor_name),
          });
        });

      unwrapPeopleList(doctors)
        .filter((d) => d?.id != null || d?.doctorId != null || d?.userId != null)
        .forEach((d) => {
          const rawName = personDisplayName(d);
          if (!rawName) return;
          const formattedName = rawName.toLowerCase().startsWith("dr")
            ? rawName
            : `Dr. ${rawName}`;
          const ids = [d.id, d.doctorId, d.userId, d.user_id].filter(
            (id) => id != null && id !== ""
          );
          ids.forEach((id) => doctorById.set(String(id), formattedName));
        });
    } catch (enrichError) {
      console.warn("[PathTransactionHistory] Enrichment skipped:", enrichError);
    }

    return list.map((tx) => {
      const isStockIn = String(tx.transactionType || "").toUpperCase() === "IN";
      const prescriptionId = getPathologyTransactionPrescriptionId(tx);
      const inventoryRecord =
        (tx?.inventoryId != null ? inventoryById.get(String(tx.inventoryId)) : null) ||
        (tx?.inventory_id != null ? inventoryById.get(String(tx.inventory_id)) : null) ||
        inventoryByItem.get(getPathologyTransactionItemName(tx).toLowerCase()) ||
        null;

      let invoiceId = getPathologyTransactionInvoiceId(tx);
      if (isStockIn) {
        invoiceId =
          invoiceId ??
          inventoryRecord?.invoiceId ??
          inventoryRecord?.invoice_id ??
          null;
      } else {
        // Use prebuilt maps (O(1)) — do not rescan the full invoice list per row.
        const currentInvoice =
          invoiceId != null ? invoiceById.get(String(invoiceId)) : null;
        if (currentInvoice && !isPathologyTestInvoice(currentInvoice)) {
          invoiceId = null;
        }
        if (invoiceId == null && prescriptionId != null) {
          invoiceId = invoiceByPrescriptionId.get(String(prescriptionId))?.id ?? null;
        }
        if (invoiceId == null) {
          const patientIdHint =
            getPathologyTransactionPatientId(tx) ??
            tx?.patientId ??
            tx?.patient_id ??
            null;
          if (patientIdHint != null) {
            invoiceId = invoiceByPatientId.get(String(patientIdHint))?.id ?? null;
          }
        }
      }

      const invoice = invoiceId != null ? invoiceById.get(String(invoiceId)) : null;
      const resolvedPrescriptionId =
        prescriptionId ??
        invoice?.prescriptionId ??
        invoice?.prescription_id ??
        null;
      const patientId =
        getPathologyTransactionPatientId(tx) ??
        invoice?.patientId ??
        invoice?.patient_id ??
        null;
      const doctorId =
        tx?.doctorId ??
        tx?.doctor_id ??
        tx?.assignedBy ??
        invoice?.doctorId ??
        invoice?.doctor_id ??
        null;
      const supplierId =
        tx?.supplierId ??
        tx?.supplier_id ??
        inventoryRecord?.supplierId ??
        inventoryRecord?.supplier_id ??
        (isStockIn ? invoice?.supplierId ?? invoice?.supplier_id : null) ??
        null;

      const patientRecord = patientId != null ? patientById.get(String(patientId)) : null;
      const resolvedPatientName =
        displayText(tx.patientName) ||
        displayText(tx.patient_name) ||
        displayText(patientRecord?.name) ||
        displayText(invoice?.patientName) ||
        "";
      const resolvedDoctorName =
        getPathologyTransactionDoctorName(tx) ||
        displayText(invoice?.doctorName) ||
        displayText(invoice?.doctor_name) ||
        (doctorId != null ? displayText(doctorById.get(String(doctorId))) : "") ||
        displayText(patientRecord?.doctorName) ||
        "";

      const stockSupplier =
        resolveSupplierDisplayName(inventoryRecord?.supplierName) ||
        resolveSupplierDisplayName(inventoryRecord?.fromName) ||
        "";
      const fromInvoice = isStockIn
        ? resolveSupplierDisplayName(invoice?.supplierName) ||
          resolveSupplierDisplayName(invoice?.supplier) ||
          resolveSupplierDisplayName(invoice?.fromName) ||
          resolveSupplierDisplayName(invoice?.from_name) ||
          ""
        : "";
      const fromSupplier =
        supplierId != null ? supplierById.get(String(supplierId)) || "" : "";
      const resolvedSupplierName =
        getPathologyTransactionSupplierName(tx) ||
        stockSupplier ||
        fromInvoice ||
        fromSupplier ||
        "";

      return {
        ...tx,
        itemName: getPathologyTransactionItemName(tx),
        invoiceId: invoiceId ?? null,
        prescriptionId: resolvedPrescriptionId,
        patientId,
        patientName: resolvedPatientName,
        doctorName: resolvedDoctorName,
        patientType:
          tx.patientType ||
          tx.patient_type ||
          (invoice?.admissionId || invoice?.admission_id ? "ipd" : tx.patientType),
        supplierName: resolvedSupplierName,
        deductedQuantity: getPathologyTransactionQuantity(tx),
      };
    });
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let apiRows = [];
      try {
        const data = await getAllPathologyTransactions();
        apiRows = Array.isArray(data) ? data : [];
      } catch (error) {
        if (!isPathologyTransactionApiUnavailable(error)) {
          console.warn("[PathTransactionHistory] API error:", error);
        }
        apiRows = [];
      }

      // Only show rows persisted in pathology_transactions — never derive from stock/lab tests.
      const enriched = await enrichWithSupplier(apiRows);
      const sorted = [...enriched].sort(
        (a, b) => new Date(b.transactionDate || 0) - new Date(a.transactionDate || 0)
      );
      setTransactions(sorted);
    } catch (error) {
      console.error("Failed to fetch pathology transactions", error);
      setTransactions([]);
    }
    setLoading(false);
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((transaction) => {
        const values = [
          transaction.id,
          getPathologyTransactionItemName(transaction),
          transaction.batchNumber,
          transaction.unitType,
          transaction.patientName,
          transaction.patientType,
          transaction.doctorName,
          getPathologyTransactionSupplierName(transaction),
          getPathologyTransactionInvoiceId(transaction),
          getPathologyTransactionPrescriptionId(transaction),
          getPathologyTransactionLabTestId(transaction),
          transaction.labTestName,
        ]
          .filter((v) => v != null && v !== "")
          .map((v) => String(v).toLowerCase());
        return values.some((v) => v.includes(q));
      });
    }

    if (filterType) {
      filtered = filtered.filter(
        (transaction) => String(transaction.transactionType || "").toUpperCase() === filterType
      );
    }

    if (filterItem.trim()) {
      const q = filterItem.toLowerCase();
      filtered = filtered.filter((transaction) =>
        getPathologyTransactionItemName(transaction).toLowerCase().includes(q)
      );
    }

    if (filterBatch.trim()) {
      const q = filterBatch.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.batchNumber || "").toLowerCase().includes(q)
      );
    }

    if (filterUnitType) {
      filtered = filtered.filter(
        (transaction) => String(transaction.unitType || "") === filterUnitType
      );
    }

    if (filterPatient.trim()) {
      const q = filterPatient.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.patientName || "").toLowerCase().includes(q)
      );
    }

    if (filterPatientType) {
      filtered = filtered.filter(
        (transaction) =>
          String(transaction.patientType || "").toLowerCase() === filterPatientType
      );
    }

    if (filterDoctor.trim()) {
      const q = filterDoctor.toLowerCase();
      filtered = filtered.filter((transaction) =>
        String(transaction.doctorName || "").toLowerCase().includes(q)
      );
    }

    if (filterSupplier) {
      filtered = filtered.filter(
        (transaction) => getPathologyTransactionSupplierName(transaction) === filterSupplier
      );
    }

    if (fromDate || toDate) {
      filtered = filtered.filter((transaction) => {
        if (!transaction.transactionDate) return false;
        const d = new Date(transaction.transactionDate);
        if (fromDate) {
          const fromStart = new Date(fromDate);
          fromStart.setHours(0, 0, 0, 0);
          if (d < fromStart) return false;
        }
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
        return true;
      });
    }

    setFilteredTransactions(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleItemNameClick = (transaction) => {
    const itemName = getPathologyTransactionItemName(transaction);
    if (!itemName) return;
    if (typeof onViewInventoryBatch === "function") {
      onViewInventoryBatch({
        itemName,
        batchNumber: transaction?.batchNumber || transaction?.batch_number || "",
        inventoryId:
          transaction?.inventoryId ??
          transaction?.inventory_id ??
          null,
        bomId: transaction?.bomId ?? transaction?.bom_id ?? null,
      });
      return;
    }
    navigate(addOrgToUrl("/inventory?tab=pathology", user));
  };

  const handleInvoiceIdClick = (transaction) => {
    const invoiceId = getPathologyTransactionInvoiceId(transaction);
    if (invoiceId == null || invoiceId === "") {
      toast.info("Invoice ID is not available for this transaction");
      return;
    }
    navigate(
      addOrgToUrl(`/invoice?invoiceId=${encodeURIComponent(invoiceId)}`, user)
    );
  };

  const handlePrescriptionIdClick = (transaction) => {
    const prescriptionId = getPathologyTransactionPrescriptionId(transaction);
    if (prescriptionId == null || prescriptionId === "") {
      toast.info("Prescription ID is not available for this transaction");
      return;
    }
    const linkedPatientId = getPathologyTransactionPatientId(transaction);
    if (linkedPatientId == null || linkedPatientId === "") {
      toast.info("Patient is not linked for this prescription");
      return;
    }
    const isIpd =
      String(transaction?.patientType || "").trim().toLowerCase() === "ipd";
    const { path, state } = buildPatientTimelineNavigation({
      user,
      patientId: linkedPatientId,
      isIpd,
      timelineTab: "prescriptions",
    });
    navigate(addOrgToUrl(path, user), state ? { state } : undefined);
  };

  const handleLabTestIdClick = (transaction) => {
    const labTestId = getPathologyTransactionLabTestId(transaction);
    const labTestName = transaction?.labTestName || "";
    if (labTestId == null && !labTestName) {
      toast.info("Lab Test ID is not available for this transaction");
      return;
    }
    if (typeof onViewLabTest === "function") {
      onViewLabTest({ labTestId, labTestName });
      return;
    }
    const inventoryTab = isPathModuleStaffRole(user?.roleName)
      ? "pathdesk"
      : "pathInventory";
    const params = new URLSearchParams({ tab: inventoryTab, pathTab: "lab" });
    if (labTestId != null && labTestId !== "") {
      params.set("labTestId", String(labTestId));
    }
    if (labTestName) params.set("labTestName", labTestName);
    navigate(addOrgToUrl(`/inventory?${params.toString()}`, user));
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("");
    setFilterItem("");
    setFilterBatch("");
    setFilterUnitType("");
    setFilterPatient("");
    setFilterPatientType("");
    setFilterDoctor("");
    setFilterSupplier("");
    setFromDate("");
    setToDate("");
  };

  const handleExport = async () => {
    const rows = filteredTransactions || [];
    const headers = [
      "Transaction ID",
      "Item Name",
      "Inventory ID",
      "Batch Number",
      "Unit Type",
      "Deducted/Added Qty",
      "Transaction Type",
      "Transaction Date",
      "Patient",
      "Patient Type",
      "Doctor",
      "Supplier Name",
      "Invoice ID",
      "Prescription ID",
      "Lab Test ID",
      "Lab Test Name",
      "Cost/Unit",
    ];
    const tableRows = rows.map((r) => [
      r.id ?? "",
      getPathologyTransactionItemName(r),
      r.inventoryId ?? r.inventory_id ?? "",
      r.batchNumber || "",
      r.unitType || "",
      getPathologyTransactionQuantity(r),
      r.transactionType,
      r.transactionDate ? dayjs(r.transactionDate).format("D MMM YYYY HH:mm") : "N/A",
      r.patientName || "N/A",
      r.patientType || "N/A",
      r.doctorName || "N/A",
      getPathologyTransactionSupplierName(r) || "N/A",
      getPathologyTransactionInvoiceId(r) || "N/A",
      getPathologyTransactionPrescriptionId(r) || "N/A",
      getPathologyTransactionLabTestId(r) || "N/A",
      r.labTestName || "N/A",
      r.costPerUnit ?? "",
    ]);

    await downloadStyledTableExcel({
      filename: `pathology_transaction_history_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Pathology Transactions",
      title: "Pathology Transaction History",
      tableHeaders: headers,
      tableRows,
    });
  };

  const formatCost = (value) => {
    if (value == null || value === "") return "N/A";
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toFixed(2);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 1.5, pt: 1, flexShrink: 0, overflow: "visible" }}>
        <Box sx={TABLE_SEARCH_ROW_SX}>
          <TextField
            placeholder="Search Transactions"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={TABLE_ICON_SX} fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterAltIcon
                fontSize="small"
                color={showFilters ? "primary" : "inherit"}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={async () => await fetchTransactions()}
              disabled={loading}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {showFilters && (
          <Box
            sx={{
              mt: 1,
              p: 2,
              bgcolor: "#f8f9fa",
              borderRadius: 2,
              overflow: "visible",
              maxWidth: "100%",
            }}
          >
            <Stack
              spacing={2}
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              sx={{ mb: 2, width: "100%" }}
            >
              <TextField
                label="Item Name"
                value={filterItem}
                onChange={(e) => setFilterItem(e.target.value)}
                size="small"
                sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: "white" }}
              />
              <TextField
                label="Batch Number"
                value={filterBatch}
                onChange={(e) => setFilterBatch(e.target.value)}
                size="small"
                sx={{ minWidth: 150, flexGrow: 1, maxWidth: 200, bgcolor: "white" }}
              />
              <TextField
                label="Unit Type"
                value={filterUnitType}
                onChange={(e) => setFilterUnitType(e.target.value)}
                select
                size="small"
                sx={{ minWidth: 150, flexGrow: 1, maxWidth: 200, bgcolor: "white" }}
              >
                <MenuItem value="">All</MenuItem>
                {[...new Set(transactions.map((t) => t.unitType).filter(Boolean))].map(
                  (type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  )
                )}
              </TextField>
              <TextField
                label="Transaction Type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                select
                size="small"
                sx={{ minWidth: 150, flexGrow: 1, maxWidth: 180, bgcolor: "white" }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="IN">IN</MenuItem>
                <MenuItem value="OUT">OUT</MenuItem>
                <MenuItem value="RETURN">RETURN</MenuItem>
              </TextField>
            </Stack>

            <Stack
              spacing={2}
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              sx={{ mb: 2, width: "100%" }}
            >
              <TextField
                label="Patient Name"
                value={filterPatient}
                onChange={(e) => setFilterPatient(e.target.value)}
                size="small"
                sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: "white" }}
              />
              <TextField
                label="Patient Type"
                value={filterPatientType}
                onChange={(e) => setFilterPatientType(e.target.value)}
                select
                size="small"
                sx={{ minWidth: 150, flexGrow: 1, maxWidth: 180, bgcolor: "white" }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="opd">OPD</MenuItem>
                <MenuItem value="ipd">IPD</MenuItem>
              </TextField>
              <TextField
                label="Doctor Name"
                value={filterDoctor}
                onChange={(e) => setFilterDoctor(e.target.value)}
                size="small"
                sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: "white" }}
              />
              <TextField
                label="Supplier Name"
                value={filterSupplier}
                onChange={(e) => setFilterSupplier(e.target.value)}
                select
                size="small"
                sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: "white" }}
              >
                <MenuItem value="">All</MenuItem>
                {[
                  ...new Set(
                    transactions
                      .map((t) => getPathologyTransactionSupplierName(t))
                      .filter(Boolean)
                  ),
                ]
                  .sort((a, b) => String(a).localeCompare(String(b)))
                  .map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>

            <Stack
              spacing={2}
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <TextField
                label="From date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: "white" }}
              />
              <TextField
                label="To date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 180, flexGrow: 1, maxWidth: 220, bgcolor: "white" }}
              />
              <Tooltip title="Clear all filters">
                <IconButton
                  onClick={clearFilters}
                  sx={{
                    bgcolor: "white",
                    "&:hover": { bgcolor: "#e0e0e0" },
                    border: "1px solid #ddd",
                  }}
                >
                  <ClearAllIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        )}
      </Box>

      <TableContainer
        sx={{
          flex: 1,
          overflowX: "auto",
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    backgroundColor: "#568af7",
                    color: "#fff",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                    minWidth: 100,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : filteredTransactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 2 }}>
                  <Alert severity="info">No data available</Alert>
                </TableCell>
              </TableRow>
            ) : (
              filteredTransactions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((transaction, index) => (
                  <TableRow key={transaction.id || `path-tx-${index}`} hover>
                    <TableCell>
                      {getPathologyTransactionItemName(transaction) ? (
                        <Box
                          component="span"
                          sx={clickableIdSx}
                          onClick={() => handleItemNameClick(transaction)}
                          title="View inventory batch info"
                        >
                          {getPathologyTransactionItemName(transaction)}
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>{transaction.batchNumber || "N/A"}</TableCell>
                    <TableCell>{transaction.unitType || "N/A"}</TableCell>
                    <TableCell>{getPathologyTransactionQuantity(transaction)}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.transactionType}
                        color={
                          transaction.transactionType === "IN"
                            ? "success"
                            : transaction.transactionType === "OUT"
                              ? "error"
                              : transaction.transactionType === "RETURN"
                                ? "warning"
                                : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {transaction.transactionDate
                        ? dayjs(transaction.transactionDate).format("D MMM YYYY hh:mm A")
                        : "N/A"}
                    </TableCell>
                    <TableCell>{transaction.patientName || "N/A"}</TableCell>
                    <TableCell>{transaction.doctorName || "N/A"}</TableCell>
                    <TableCell>
                      {getPathologyTransactionSupplierName(transaction) || "N/A"}
                    </TableCell>
                    <TableCell>
                      {getPathologyTransactionInvoiceId(transaction) ? (
                        <Box
                          component="span"
                          sx={clickableIdSx}
                          onClick={() => handleInvoiceIdClick(transaction)}
                          title="Open invoice"
                        >
                          {getPathologyTransactionInvoiceId(transaction)}
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      {getPathologyTransactionPrescriptionId(transaction) ? (
                        <Box
                          component="span"
                          sx={clickableIdSx}
                          onClick={() => handlePrescriptionIdClick(transaction)}
                          title="Open prescription"
                        >
                          {getPathologyTransactionPrescriptionId(transaction)}
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      {getPathologyTransactionLabTestId(transaction) ? (
                        <Box
                          component="span"
                          sx={clickableIdSx}
                          onClick={() => handleLabTestIdClick(transaction)}
                          title="Open lab test"
                        >
                          {getPathologyTransactionLabTestId(transaction)}
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>{formatCost(transaction.costPerUnit)}</TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredTransactions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ flexShrink: 0, borderTop: "1px solid #e0e0e0" }}
      />
    </Box>
  );
};

export default PathTransactionHistory;
